import React, { useEffect, useState } from "react";
import API_BASE_URL from "../config";
import "./SocialAuthButtons.css";

// Human-readable copy for the ?error= codes the OAuth callback can redirect back with.
const ERROR_MESSAGES = {
  social_cancelled: "Sign-in was cancelled.",
  social_no_email: "That account didn't share an email address. Grant email permission, or sign up with email instead.",
  social_email_unverified: "That account's email isn't verified yet. Verify it with the provider and try again.",
  invalid_state: "That sign-in link expired. Please try again.",
  missing_code: "Sign-in didn't complete. Please try again.",
  social_signin_failed: "Couldn't complete sign-in. Please try again.",
  unsupported_provider: "That sign-in method isn't available.",
  google_not_configured: "Google sign-in isn't set up yet.",
  facebook_not_configured: "Facebook sign-in isn't set up yet.",
};

const GoogleIcon = () => (
  <svg viewBox="0 0 18 18" width="18" height="18" aria-hidden="true">
    <path fill="#4285F4" d="M17.64 9.2c0-.64-.06-1.25-.16-1.84H9v3.48h4.84a4.14 4.14 0 0 1-1.8 2.72v2.26h2.92c1.7-1.57 2.68-3.88 2.68-6.62z" />
    <path fill="#34A853" d="M9 18c2.43 0 4.47-.8 5.96-2.18l-2.92-2.26c-.8.54-1.84.86-3.04.86-2.34 0-4.32-1.58-5.03-3.7H.96v2.33A9 9 0 0 0 9 18z" />
    <path fill="#FBBC05" d="M3.97 10.72a5.4 5.4 0 0 1 0-3.44V4.95H.96a9 9 0 0 0 0 8.1l3.01-2.33z" />
    <path fill="#EA4335" d="M9 3.58c1.32 0 2.5.45 3.44 1.35l2.58-2.58C13.46.9 11.43 0 9 0A9 9 0 0 0 .96 4.95l3.01 2.33C4.68 5.16 6.66 3.58 9 3.58z" />
  </svg>
);

const FacebookIcon = () => (
  <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true" fill="#fff">
    <path d="M24 12.07C24 5.4 18.63 0 12 0S0 5.4 0 12.07C0 18.1 4.39 23.1 10.13 24v-8.44H7.08v-3.49h3.05V9.41c0-3.02 1.79-4.69 4.53-4.69 1.31 0 2.68.24 2.68.24v2.96h-1.51c-1.49 0-1.956.93-1.956 1.89v2.26h3.33l-.53 3.49h-2.8V24C19.61 23.1 24 18.1 24 12.07z" />
  </svg>
);

const PROVIDER_UI = {
  google: { label: "Continue with Google", icon: <GoogleIcon />, className: "social-btn-google" },
  facebook: { label: "Continue with Facebook", icon: <FacebookIcon />, className: "social-btn-facebook" },
};

/**
 * Renders the configured social sign-in buttons. Each one is a plain top-level
 * navigation to the API's OAuth start endpoint — no client secret in the browser.
 * Renders nothing if the server reports no configured providers.
 */
function SocialAuthButtons({ dividerText = "or" }) {
  const [providers, setProviders] = useState([]);
  const [error, setError] = useState("");

  useEffect(() => {
    fetch(`${API_BASE_URL}/api/auth/providers`)
      .then((r) => r.json())
      .then((data) => setProviders(Array.isArray(data.providers) ? data.providers : []))
      .catch(() => setProviders([]));

    const code = new URLSearchParams(window.location.search).get("error");
    if (code) setError(ERROR_MESSAGES[code] || "Sign-in failed. Please try again.");
  }, []);

  const startSignIn = (providerId) => {
    // Carry the pricing plan the user clicked through the round trip, so they land
    // back on the right checkout page instead of the dashboard.
    const plan = new URLSearchParams(window.location.search).get("plan");
    const params = new URLSearchParams();
    if (plan) params.set("plan", plan);

    // Desktop can't host the consent screen: providers refuse to render inside an
    // embedded view, and the user should be able to see the address bar and use
    // their password manager. It goes to the system browser and the API sends the
    // session home over a photentra:// link.
    if (window.photentra?.isDesktop) {
      params.set("platform", "desktop");
      window.photentra.openExternal(
        `${API_BASE_URL}/api/auth/${providerId}?${params}`,
      );
      return;
    }

    const query = params.toString() ? `?${params}` : "";
    window.location.href = `${API_BASE_URL}/api/auth/${providerId}${query}`;
  };

  if (providers.length === 0) {
    return error ? <div className="error-message">{error}</div> : null;
  }

  return (
    <div className="social-auth">
      {error && <div className="error-message">{error}</div>}

      <div className="social-divider">
        <span>{dividerText}</span>
      </div>

      {providers.map((provider) => {
        const ui = PROVIDER_UI[provider.id];
        if (!ui) return null;
        return (
          <button
            key={provider.id}
            type="button"
            className={`social-btn ${ui.className}`}
            onClick={() => startSignIn(provider.id)}
          >
            {ui.icon}
            <span>{ui.label}</span>
          </button>
        );
      })}
    </div>
  );
}

export default SocialAuthButtons;
